import { NavLink, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ETIQUETA_ROL_OPERADOR,
  formatearMoneda,
  type PermisoOperador,
  type ResumenPlataforma,
} from '@cmms/shared';
import { api } from '../../servicios/api.js';
import { useSesionOperador } from '../../servicios/sesion-operador.js';
import { Marca } from '../../componentes/marca.js';
import { Cargando, Indicador } from '../../componentes/piezas.js';
import {
  IconoActividad,
  IconoAlerta,
  IconoLicencia,
  IconoUbicacion,
  IconoUsuarios,
} from '../../componentes/iconos.js';
import { AccesoOperador } from './acceso-operador.js';
import { DetalleTenant, ListaTenants } from './tenants.js';
import { ListaPlanes } from './planes.js';
import { Bitacora, ListaOperadores } from './operadores.js';
import { ListaVersionesApp } from './versiones-app.js';

const ENTRADAS: { ruta: string; nombre: string; icono: JSX.Element; permiso: PermisoOperador }[] = [
  { ruta: '/plataforma', nombre: 'Panel', icono: <IconoLicencia />, permiso: 'tenant:leer' },
  { ruta: '/plataforma/organizaciones', nombre: 'Organizaciones', icono: <IconoUbicacion />, permiso: 'tenant:leer' },
  { ruta: '/plataforma/planes', nombre: 'Planes', icono: <IconoLicencia />, permiso: 'plan:leer' },
  { ruta: '/plataforma/operadores', nombre: 'Operadores', icono: <IconoUsuarios />, permiso: 'operador:leer' },
  { ruta: '/plataforma/versiones-app', nombre: 'Versiones de la app', icono: <IconoAlerta />, permiso: 'version_app:leer' },
  { ruta: '/plataforma/bitacora', nombre: 'Bitácora', icono: <IconoActividad />, permiso: 'tenant:leer' },
];

/**
 * Consola de plataforma.
 *
 * Vive bajo `/plataforma` con su propia sesión, y por eso no cuelga de la
 * `Disposicion` del producto: un operador no tiene tenant, así que la cabecera
 * que anuncia la organización en curso no tendría qué mostrar. Comparte los
 * estilos, no la navegación.
 */
export function ConsolaPlataforma(): JSX.Element {
  const { operador, cargando } = useSesionOperador();

  if (cargando) return <Cargando texto="Restaurando sesión de plataforma…" />;
  if (!operador) return <AccesoOperador />;

  return (
    <Routes>
      <Route element={<Disposicion />}>
        <Route index element={<Panel />} />
        <Route path="organizaciones" element={<ListaTenants />} />
        <Route path="organizaciones/:id" element={<DetalleTenant />} />
        <Route path="planes" element={<ListaPlanes />} />
        <Route path="operadores" element={<ListaOperadores />} />
        <Route path="versiones-app" element={<ListaVersionesApp />} />
        <Route path="bitacora" element={<Bitacora />} />
        <Route path="*" element={<Navigate to="/plataforma" replace />} />
      </Route>
    </Routes>
  );
}

function Disposicion(): JSX.Element {
  const { operador, puede, salir } = useSesionOperador();
  const ubicacion = useLocation();

  const iniciales = (operador?.nombreCompleto ?? '')
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0] ?? '')
    .join('')
    .toUpperCase();

  const visibles = ENTRADAS.filter((e) => puede(e.permiso));
  const actual = visibles.find((e) => e.ruta === ubicacion.pathname);

  return (
    <div className="app">
      <nav className="barra-lateral" aria-label="Navegación de plataforma">
        <div className="marca">
          <Marca />
        </div>

        <div className="nav-grupo">
          <div className="nav-grupo-titulo">PLATAFORMA</div>
          {visibles.map((entrada) => (
            <NavLink
              key={entrada.ruta}
              to={entrada.ruta}
              end={entrada.ruta === '/plataforma'}
              className={({ isActive }) => `nav-enlace${isActive ? ' activo' : ''}`}
            >
              {entrada.icono}
              <span className="nav-enlace-texto">{entrada.nombre}</span>
            </NavLink>
          ))}
        </div>

        <div className="nav-grupo">
          <div className="nav-grupo-titulo">PRODUCTO</div>
          {/* Salida explícita al producto. Es un enlace normal, no un atajo de
              sesión: entrar a una organización exige sus propias credenciales. */}
          <a className="nav-enlace" href="/">
            <IconoUbicacion />
            <span className="nav-enlace-texto">Ir al acceso de organización</span>
          </a>
        </div>
      </nav>

      <div className="principal">
        <header className="barra-superior">
          <div className="migas">
            Plataforma · <strong>{actual?.nombre ?? 'Organización'}</strong>
          </div>

          <div className="usuario-chip">
            <div className="avatar" aria-hidden="true">
              {iniciales || '?'}
            </div>
            <div className="usuario-datos">
              <div className="usuario-nombre">{operador?.nombreCompleto}</div>
              <div className="usuario-rol">{operador ? ETIQUETA_ROL_OPERADOR[operador.rol] : ''}</div>
            </div>
            <button type="button" className="boton secundario pequeno" onClick={() => void salir()}>
              Salir
            </button>
          </div>
        </header>

        <main className="contenido">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function Panel(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['plataforma', 'resumen'],
    queryFn: () => api.get<ResumenPlataforma>('/plataforma/resumen'),
  });

  if (isLoading) return <Cargando />;
  if (!data) return <Cargando texto="Sin datos" />;

  const ocupacion =
    data.asientosContratados > 0 ? Math.round((data.asientosConsumidos / data.asientosContratados) * 100) : 0;

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Panel de plataforma</h1>
          <p>Padrón de organizaciones y licenciamiento agregado, en soles.</p>
        </div>
      </div>

      <div className="rejilla-indicadores">
        <Indicador
          titulo="ORGANIZACIONES ACTIVAS"
          valor={data.tenantsActivos}
          nota={data.tenantsSuspendidos > 0 ? `${data.tenantsSuspendidos} suspendida(s)` : 'Ninguna suspendida'}
        />
        <Indicador
          titulo="ASIENTOS EN USO"
          valor={`${data.asientosConsumidos} / ${data.asientosContratados}`}
          nota={`${ocupacion}% de lo contratado`}
        />
        <Indicador
          titulo="INGRESO MENSUAL RECURRENTE"
          valor={formatearMoneda(data.ingresoMensualRecurrentePen)}
          nota="Solo suscripciones activas, sin IGV"
        />
        {/* El dato que más sirve para vender un upgrade: cada uno es un alta que
            un cliente intentó y la licencia rechazó. */}
        <Indicador
          titulo="ALTAS BLOQUEADAS (30 D)"
          valor={data.intentosBloqueadosUltimos30Dias}
          nota="Intentos rechazados por falta de asiento"
        />
      </div>
    </>
  );
}
